import { useState, useEffect } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  MenuItem, Stack, Alert, CircularProgress, Divider,
  InputAdornment, Paper, ToggleButton, ToggleButtonGroup,
  Checkbox, FormControlLabel, Collapse, Chip, IconButton, Autocomplete
} from "@mui/material";
import CurrencyRupeeIcon      from "@mui/icons-material/CurrencyRupee";
import PendingActionsIcon     from "@mui/icons-material/PendingActions";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PersonIcon             from "@mui/icons-material/Person";
import PaymentIcon            from "@mui/icons-material/Payment";
import SwapHorizIcon          from "@mui/icons-material/SwapHoriz";
import CardGiftcardIcon       from "@mui/icons-material/CardGiftcard";
import ReceiptIcon            from "@mui/icons-material/Receipt";
import AccountBalanceIcon     from "@mui/icons-material/AccountBalance";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CloseIcon              from "@mui/icons-material/Close";
import api from "../service/api";

const LOCATIONS   = ["Khejuri", "Chaitanyapur", "Nandigram", "Ms Enterprise"];
const FINANCE_COS = [
  "bajaj auto credit ltd", "Tata capital", "IDFC first bank","HDB financial services Ltd",
  "Shriram Finance Limited", "ujjivan small finance Bank Limited","HDFC BANK LTD."
];
 
const rupee   = <CurrencyRupeeIcon sx={{ fontSize: 14 }} />;
const qtyIcon = <Inventory2OutlinedIcon sx={{ fontSize: 14 }} />;

const updateSoldBike = async (data) => {
  const res = await api.put("/update-sold-bikes", data);
  return res.data;
};

// ── Section Header — defined OUTSIDE to avoid re-mount on re-render ──────────
const Section = ({ icon, title, color = "#1976d2" }) => (
  <Box display="flex" alignItems="center" gap={1} mt={2} mb={1}>
    <Box sx={{ color, display: "flex" }}>{icon}</Box>
    <Typography
      variant="subtitle2" fontWeight={700} letterSpacing={1}
      sx={{ color, textTransform: "uppercase", fontSize: "0.72rem" }}
    >
      {title}
    </Typography>
    <Box flex={1} height="1px" bgcolor={`${color}33`} ml={1} />
  </Box>
);

// ── Reusable Field — defined OUTSIDE to avoid cursor loss on re-render ────────
const F = ({
  label, name, type = "text", xs = 12, md = 6,
  select, children, startAdornment, multiline, rows,
  form, onChange, error, helperText
}) => (
  <Grid item xs={xs} md={md}>
    <TextField
      fullWidth size="small" label={label} name={name}
      type={type} value={form[name] ?? ""} onChange={onChange}
      select={select} multiline={multiline} rows={rows}
      error={!!error} helperText={error || helperText}
      InputLabelProps={type === "date" ? { shrink: true } : undefined}
      InputProps={startAdornment ? {
        startAdornment: <InputAdornment position="start">{startAdornment}</InputAdornment>
      } : undefined}
      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
    >
      {select && children}
    </TextField>
  </Grid>
);

// ── Exchange Field — reads from exchange sub-object ───────────────────────────
const EF = ({
  label, name, type = "text", md = 6,
  select, children, startAdornment,
  exchange, onExchangeChange, error
}) => (
  <Grid item xs={12} md={md}>
    <TextField
      fullWidth size="small" label={label} name={name}
      type={type} value={exchange[name] ?? ""} onChange={onExchangeChange}
      select={select} error={!!error} helperText={error}
      InputProps={startAdornment ? {
        startAdornment: <InputAdornment position="start">{startAdornment}</InputAdornment>
      } : undefined}
      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
    >
      {select && children}
    </TextField>
  </Grid>
);

// ─────────────────────────────────────────────────────────────────────────────

const UpdateSoldBikeForm = ({ sale, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [success, setSuccess] = useState("");
  const [errors,  setErrors]  = useState({});

  // 🎁 NEW: known gift names, fetched once, used to power the autocomplete below
  const [giftOptions, setGiftOptions] = useState([]);

  useEffect(() => {
    let active = true;
    api.get("/accessories")
      .then((res) => {
        if (!active) return;
        const rows = res.data?.data || res.data || [];
        const names = rows
          .filter((a) => a.itemType === "Gift" && a.giftName)
          .map((a) => a.giftName);
        setGiftOptions([...new Set(names)]);
      })
      .catch(() => { /* ignore — free text still works without suggestions */ });
    return () => { active = false; };
  }, []);

  const [form, setForm] = useState({
    _id: "",
    // customer
    customerName: "", fatherName: "", mobile: "", mobile2: "",
    address: "", village: "", postOffice: "",
    policeStation: "", pinCode: "", landmark: "", GSTIN: "", regNo:"",
    // sale
    salePrice: "", orp: "", saleDate: "", location: "",
    // payment mode toggles
    isCash: true, isFinance: false, isExchange: false,
    cashAmount: "",DP:"",isDP:true,
    // finance
    financeCompany: "", financeAmount: "",
    // exchange (sub-object)
    exchange: {
      ownerName: "", location: "", model: "",
      registrationNumber: "", exchangePrice: "", phone: ""
    },
    // expenses
    helmet: "", seatCover: "", bikeCover: "",petrol:"",legguard:"",
    transport: "", broker: "", tcr: "", regBroker: "", amcRsa: "",
    // gifts
    giftName: "", giftQty: "",
    // charges
    rto: "", insurance: "",
    // misc
    discount: "", couponCashback: "",
    salesmanName: "", salesmanIncentive: "",
    remarks: "",
    // due
    dueStatus: "PAID", dueTotalAmount: "",
  });

  // ── Pre-fill from sale prop ────────────────────────────────────────────────
  useEffect(() => {
    if (!sale) return;

    const st          = sale.saleType || "";
    const hasFinance  = ["FINANCE", "EX+FIN"].includes(st);
    const hasExchange = ["EXCHANGE", "EX+FIN", "CASH+EX"].includes(st);
    const hasCash = true;


    setForm({
      _id:           sale._id          || "",
      customerName:  sale.customerName || "",
      fatherName:    sale.fatherName   || "",
      mobile:        Array.isArray(sale.mobile) ? (sale.mobile[0] || "") : (sale.mobile || ""),
      mobile2:       Array.isArray(sale.mobile) ? (sale.mobile[1] || "") : "",
      address:       sale.address       || "",
      village:       sale.village       || "",
      postOffice:    sale.postOffice    || "",
      policeStation: sale.policeStation || "",
      pinCode:       sale.pinCode       || "",
      landmark:      sale.landmark      || "",
      GSTIN:         sale.GSTIN         || "",
      regNo:   sale.regNo         || "",
      salePrice:     sale.salePrice     ?? "",
      orp:           sale.orp           ?? "",
      saleDate:      sale.saleDate
        ? new Date(sale.saleDate).toISOString().split("T")[0]
        : "",
      location:      sale.location || "",
      // payment toggles derived from saleType
      isCash:     hasCash,
       isFinance:  hasFinance,
      isExchange: hasExchange,
      cashAmount: sale.cashAmount ?? "",
      DP:sale.DP ?? "",
      // finance
      financeCompany: hasFinance ? (sale.finance?.companyName || "") : "",
      financeAmount:  hasFinance ? (sale.finance?.amount      ?? "") : "",
      // exchange sub-object
      exchange: {
        ownerName:          hasExchange ? (sale.exchange?.ownerName          || "") : "",
        location:           hasExchange ? (sale.exchange?.location           || "") : "",
        model:              hasExchange ? (sale.exchange?.model              || "") : "",
        registrationNumber: hasExchange ? (sale.exchange?.registrationNumber || "") : "",
        exchangePrice:      hasExchange ? (sale.exchange?.exchangePrice      ?? "") : "",
        phone:              hasExchange ? (sale.exchange?.phone              || "") : "",
      },
      // expenses
      helmet:    sale.expenses?.helmet    ?? "",
      seatCover: sale.expenses?.seatCover ?? "",
      bikeCover: sale.expenses?.bikeCover ?? "",
       petrol: sale.expenses?.petrol ?? "",
        legguard: sale.expenses?.legguard ?? "",
    transport: sale.expenses?.transport ?? "",
      broker:    sale.expenses?.broker    ?? "",
      tcr:       sale.expenses?.tcr       ?? "",
      regBroker: sale.expenses?.regBroker ?? "",
      amcRsa:    sale.expenses?.amcRsa    ?? "",
      // gifts
      giftName: sale.gifts?.[0]?.name     || "",
      giftQty:  sale.gifts?.[0]?.quantity ?? "",
      // charges
      rto:       sale.charges?.rto       ?? "",
      insurance: sale.charges?.insurance ?? "",
      // misc
      discount:          sale.discount           ?? "",
      couponCashback:    sale.couponCashback      ?? "",
      salesmanName:      sale.salesman?.name      || "",
      salesmanIncentive: sale.salesman?.incentive ?? "",
      remarks:           sale.remarks             || "",
      // due
      dueStatus:      sale.due?.status      || "PAID",
      dueTotalAmount: sale.due?.totalAmount ?? "",
    });
  }, [sale]);
//update sold bike form 
  // ── Handlers ───────────────────────────────────────────────────────────────
  const set = (e) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
    if (errors[e.target.name])
      setErrors((p) => { const n = { ...p }; delete n[e.target.name]; return n; });
  };

  const setExchange = (e) => {
    setForm((p) => ({ ...p, exchange: { ...p.exchange, [e.target.name]: e.target.value } }));
    if (errors[e.target.name])
      setErrors((p) => { const n = { ...p }; delete n[e.target.name]; return n; });
  };

const toggle = (key) => {
  setForm((p) => {
    if (key === "isCash") return p; // ← CASH checkbox is permanently locked ON
    
    const updated = { ...p, [key]: !p[key] };
    if (key === "isExchange" && p.isExchange) {
      updated.exchange = {
        ownerName: "", location: "", model: "",
        registrationNumber: "", exchangePrice: "", phone: ""
      };
    }
        if (key === "isFinance" && p.isFinance) {
      updated.financeCompany = "";
      updated.financeAmount  = "";
    }

    return updated;
  });
};
  const handleDueStatus = (_, val) => {
    if (val) setForm((p) => ({ ...p, dueStatus: val, dueTotalAmount: "" }));
  };

  // ── Derived saleType from checkboxes ──────────────────────────────────────
const derivedSaleType = () => {
  const cash     = Number(form.cashAmount) > 0;
  const DP  = Number(form.DP) > 0;
  const finance  = form.isFinance;
  const exchange = form.isExchange;

  if (finance && exchange) return "EX+FIN";
  if (cash    && exchange) return "CASH+EX";
  if (finance)             return "FINANCE";
  if (exchange)            return "EXCHANGE";
  return "CASH";
};

  // ── Net display ────────────────────────────────────────────────────────────
  const netDisplay =
    Number(form.orp || 0) - Number(form.discount || 0) - Number(form.couponCashback || 0);

  const dueRemaining =
    form.dueStatus === "DUE" && form.dueTotalAmount
      ? Number(form.dueTotalAmount)
      : null;

  // ── Validate ───────────────────────────────────────────────────────────────
  const validate = () => {
    const temp = {};
    if (!form.customerName.trim()) temp.customerName = "Required";
    if (!form.mobile.trim())       temp.mobile       = "Required";
    if (!form.salePrice)           temp.salePrice    = "Required";
    if (!form.orp)                 temp.orp          = "Required";
    if (!form.saleDate)            temp.saleDate     = "Required";
    if (!form.location)            temp.location     = "Required";
    
    if (form.isFinance) {
      if (!form.financeCompany.trim()) temp.financeCompany = "Required";
      if (!form.financeAmount)         temp.financeAmount  = "Required";
    }
 if (form.isExchange) {
  const reg = form.exchange.registrationNumber?.trim();
  const originalReg = sale?.exchange?.registrationNumber?.trim() || "";

  if (!form.exchange.ownerName.trim())
    temp.ownerName = "Required";

  if (!reg)
    temp.registrationNumber = "Required";
  else if (reg.length < 5 && reg !== originalReg)  // ← skip length check if unchanged
    temp.registrationNumber = "Invalid registration";

  if (!form.exchange.exchangePrice)
    temp.exchangePrice = "Required";
}
    if (form.dueStatus === "DUE" && !form.dueTotalAmount)
      temp.dueTotalAmount = "Required";
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleUpdate = async () => {
    setError(""); setSuccess("");
    if (!form._id) { setError("Sale ID missing."); return; }
    if (!validate()) return;

 
  // =============================
// ✅ FIX 4: FINAL PAYLOAD (MOST IMPORTANT)
// =============================
const payload = {
  _id: form._id,

  customerName: form.customerName,
  fatherName: form.fatherName,
 
mobile: [form.mobile, form.mobile2]
  .filter(m => m && m.trim() !== ""),

  address: form.address,
  village: form.village,
  postOffice: form.postOffice,
  policeStation: form.policeStation,
  pinCode: form.pinCode,
  landmark: form.landmark,
  GSTIN: form.GSTIN,
  regNo:form.regNo,

  salePrice: Number(form.salePrice) || undefined,
  orp: Number(form.orp) || undefined,
  saleDate: form.saleDate || undefined,
  location: form.location || undefined,
    saleType: derivedSaleType(),   

  // 🔥 DO NOT SEND saleType (backend handles it better)
  // saleType,

cashAmount: Number(form.cashAmount) || 0,
DP: Number(form.DP) || 0,
 


  discount: Number(form.discount) || 0,
  couponCashback: Number(form.couponCashback) || 0,

remarks: form.remarks,

 dueAmount:
  form.dueStatus === "DUE"
    ? Number(form.dueTotalAmount) || 0
    : 0,

  // =========================
  // ✅ FINANCE
  // =========================
 
    finance: form.isFinance
  ? {
      companyName: form.financeCompany,
      amount: Number(form.financeAmount) || 0,
    }
  : { companyName: "", amount: 0 }, 

  // =========================
  // 🔥 EXCHANGE (CRITICAL FIX)
  // =========================
  exchange: form.isExchange
    ? {
        ownerName: form.exchange.ownerName,
        model: form.exchange.model,
        registrationNumber: form.exchange.registrationNumber,
        phone: form.exchange.phone,
        location: form.exchange.location,
        exchangePrice: Number(form.exchange.exchangePrice) || 0,
      }
    : {
        registrationNumber: "" // 🔥 REQUIRED FOR REMOVE
      },

  // =========================
  // EXPENSES
  // =========================
  expenses: {
    helmet: Number(form.helmet) || 0,
    seatCover: Number(form.seatCover) || 0,
    bikeCover: Number(form.bikeCover) || 0,
    petrol: Number(form.petrol) || 0,
      legguard: Number(form.legguard) || 0,
    transport: Number(form.transport) || 0,
    broker: Number(form.broker) || 0,
    tcr: Number(form.tcr) || 0,
    regBroker: Number(form.regBroker) || 0,
    amcRsa: Number(form.amcRsa) || 0,
  },

  // =========================
  // GIFTS
  // =========================
  gifts: form.giftName
    ? [{ name: form.giftName, quantity: Number(form.giftQty) || 1 }]
    : [],

  // =========================
  // CHARGES
  // =========================
  charges: {
    rto: Number(form.rto) || 0,
    insurance: Number(form.insurance) || 0,
  },

  // =========================
  // SALESMAN
  // =========================
  salesman: {
    name: form.salesmanName,
    incentive: Number(form.salesmanIncentive) || 0,
  },
};

    setLoading(true);
    try {
      await updateSoldBike(payload);
      setSuccess("Sale record updated successfully!");
      if (onSuccess) onSuccess();
      setTimeout(() => onClose(), 1200);
    } catch (err) {
      setError(err?.response?.data?.message || "Update failed.");
    } finally {
      setLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box
      p={3}
      sx={{
        maxHeight: "90vh", overflowY: "auto",
        background: "linear-gradient(145deg, #f8faff 0%, #fff 100%)",
        "&::-webkit-scrollbar": { width: 4 },
        "&::-webkit-scrollbar-thumb": { borderRadius: 2, bgcolor: "#c5d5ea" }
      }}
    >
      {/* ── HEADER ── */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Box>
          <Typography variant="h6" fontWeight={800} color="#0d1b2a">✏️ Update Sale Record</Typography>
          <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
            {sale?.customerName && <Chip label={sale.customerName} size="small" color="primary" variant="outlined" />}
            {sale?.model        && <Chip label={sale.model}        size="small" variant="outlined" />}
            {sale?.invoiceNo    && <Chip label={sale.invoiceNo}    size="small" sx={{ bgcolor: "#e3f2fd" }} />}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </Box>

      {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {Object.keys(errors).length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Please fix {Object.keys(errors).length} error(s) before saving.
        </Alert>
      )}

      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={1.5}>

        {/* ── CUSTOMER ── */}
        <Grid item xs={12}>
          <Section icon={<PersonIcon fontSize="small" />} title="Customer Details" color="#1565c0" />
        </Grid>
        <F label="Customer Name *"   name="customerName"  form={form} onChange={set} error={errors.customerName} md={6} />
        <F label="Father Name (C/O)" name="fatherName"    form={form} onChange={set} md={6} />
        <F label="Mobile *"          name="mobile"        form={form} onChange={set} error={errors.mobile} md={6} />
        <F label="Alternate Mobile"  name="mobile2"       form={form} onChange={set} md={6} />
        <F label="Address"           name="address"       form={form} onChange={set} xs={12} md={12} />
        <F label="Village"           name="village"       form={form} onChange={set} md={4} />
        <F label="Post Office"       name="postOffice"    form={form} onChange={set} md={4} />
        <F label="Police Station"    name="policeStation" form={form} onChange={set} md={4} />
        <F label="PIN Code"          name="pinCode"       form={form} onChange={set} type="number" md={4} />
        <F label="Landmark"          name="landmark"      form={form} onChange={set} md={8} />
        <F label="GSTIN"             name="GSTIN"         form={form} onChange={set} md={6} />
        <F label="Reg. No"             name="regNo"         form={form} onChange={set} md={6} />
         

        {/* ── SALE DETAILS ── */}
        <Grid item xs={12}>
          <Section icon={<CurrencyRupeeIcon fontSize="small" />} title="Sale Details" color="#2e7d32" />
        </Grid>
        <F label="ExShowroom Price *" name="salePrice" form={form} onChange={set}
          error={errors.salePrice} type="number" startAdornment={rupee} md={4} />
        <F label="ORP *" name="orp" form={form} onChange={set}
          error={errors.orp} type="number" startAdornment={rupee} md={4} />
        <F label="Sale Date *" name="saleDate" form={form} onChange={set}
          error={errors.saleDate} type="date" md={4} />
        <F label="Location *" name="location" form={form} onChange={set}
          error={errors.location} select md={4}>
          {LOCATIONS.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
        </F>

        {/* ── PAYMENT MODE ── */}
        <Grid item xs={12}>
          <Section icon={<PaymentIcon fontSize="small" />} title="Payment Mode" color="#6a1b9a" />
        </Grid>
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ px: 2, py: 1, borderRadius: 2, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            <FormControlLabel
              control={<Checkbox checked={form.isCash} onChange={() => toggle("isCash")} size="small" sx={{ color: "#2e7d32" }} />}
              label={<Typography variant="body2" fontWeight={600} color="#2e7d32">CASH</Typography>}
            />
            <FormControlLabel
              control={<Checkbox checked={form.isFinance} onChange={() => toggle("isFinance")} size="small" sx={{ color: "#1565c0" }} />}
              label={<Typography variant="body2" fontWeight={600} color="#1565c0">FINANCE</Typography>}
            />
            <FormControlLabel
              control={<Checkbox checked={form.isExchange} onChange={() => toggle("isExchange")} size="small" sx={{ color: "#e65100" }} />}
              label={<Typography variant="body2" fontWeight={600} color="#e65100">EXCHANGE</Typography>}
            />
            <Chip
              size="small" variant="outlined"
              label={`Type: ${derivedSaleType()}`}
              sx={{ ml: "auto", fontWeight: 700, borderColor: "#9c27b0", color: "#9c27b0" }}
            />
          </Paper>
        </Grid>

        {/* CASH AMOUNT */}
        <Collapse in={form.isCash} style={{ width: "100%" }}>
          <Grid container spacing={1.5} sx={{ pl: 1.5, pr: 0.75, mt: 0 }}>
            <F label="Cash Amount *" name="cashAmount" form={form} onChange={set}
              error={errors.cashAmount} type="number" startAdornment={rupee} md={6} /> 
              <F label="DP Amount *" name="DP" form={form} onChange={set}
              error={errors.DP} type="number" startAdornment={rupee} md={6} />
          </Grid>
        </Collapse>

        {/* FINANCE */}
        <Collapse in={form.isFinance} style={{ width: "100%" }}>
          <Grid container spacing={1.5} sx={{ pl: 1.5, pr: 0.75, mt: 0 }}>
            <Grid item xs={12}>
              <Section icon={<AccountBalanceIcon fontSize="small" />} title="Finance Details" color="#1565c0" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select fullWidth size="small" label="Finance Company *"
                name="financeCompany" value={form.financeCompany} onChange={set}
                error={!!errors.financeCompany} helperText={errors.financeCompany}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              >
                {FINANCE_COS.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
              </TextField>
            </Grid>
            <F label="Finance Amount *" name="financeAmount" form={form} onChange={set}
              error={errors.financeAmount} type="number" startAdornment={rupee} md={6} />
          </Grid>
        </Collapse>

        {/* EXCHANGE */}
        <Collapse in={form.isExchange} style={{ width: "100%" }}>
          <Grid container spacing={1.5} sx={{ pl: 1.5, pr: 0.75, mt: 0 }}>
            <Grid item xs={12}>
              <Section icon={<SwapHorizIcon fontSize="small" />} title="Exchange Vehicle" color="#e65100" />
            </Grid>
            <EF label="Owner Name *"      name="ownerName"          exchange={form.exchange} onExchangeChange={setExchange} error={errors.ownerName} md={6} />
            <EF label="Phone"             name="phone"              exchange={form.exchange} onExchangeChange={setExchange} md={6} />
            <EF label="Model"             name="model"              exchange={form.exchange} onExchangeChange={setExchange} md={6} />
            <EF label="Registration No *" name="registrationNumber" exchange={form.exchange} onExchangeChange={setExchange} error={errors.registrationNumber} md={6} />
            <EF label="Exchange Price *"  name="exchangePrice"      exchange={form.exchange} onExchangeChange={setExchange}
              error={errors.exchangePrice} type="number" startAdornment={rupee} md={6} />
            <Grid item xs={12} md={6}>
              <TextField
                select fullWidth size="small" label="Exchange Location"
                name="location" value={form.exchange.location} onChange={setExchange}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              >
                <MenuItem value="">None</MenuItem>
                {LOCATIONS.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
        </Collapse>

        {/* ── EXPENSES ── */}
        <Grid item xs={12}>
          <Section icon={<CardGiftcardIcon fontSize="small" />} title="Expenses / Freebies" color="#00838f" />
        </Grid>
        <F label="Helmet (qty)"     name="helmet"    form={form} onChange={set} type="number" md={4} startAdornment={qtyIcon} />
        <F label="Seat Cover (qty)" name="seatCover" form={form} onChange={set} type="number" md={4} startAdornment={qtyIcon} />
        <F label="Bike Cover (qty)" name="bikeCover" form={form} onChange={set} type="number" md={4} startAdornment={qtyIcon} />
         <F label="Petrol (₹)"   name="petrol"  form={form} onChange={set} type="number" md={4} startAdornment={rupee} />
       <F label="Leg Guard (₹)"   name="legguard"  form={form} onChange={set} type="number" md={4} startAdornment={rupee} />
       <F label="Transport (₹)"   name="transport"  form={form} onChange={set} type="number" md={4} startAdornment={rupee} />
        <F label="Broker (₹)"      name="broker"     form={form} onChange={set} type="number" md={4} startAdornment={rupee} />
        <F label="TCR (₹)"         name="tcr"        form={form} onChange={set} type="number" md={4} startAdornment={rupee} />
        <F label="Reg Broker (₹)"  name="regBroker"  form={form} onChange={set} type="number" md={4} startAdornment={rupee} />
        <F label="AMC/RSA (₹)"     name="amcRsa"     form={form} onChange={set} type="number" md={4} startAdornment={rupee} />

        {/* ── GIFTS ── */}
        <Grid item xs={12}>
          <Section icon={<CardGiftcardIcon fontSize="small" />} title="Gifts" color="#ad1457" />
        </Grid>
        {/* 🎁 CHANGED: autocomplete suggests every gift name used before, freeSolo still allows a brand-new one */}
        <Grid item xs={12} md={8}>
          <Autocomplete
            freeSolo
            size="small"
            options={giftOptions}
            value={form.giftName}
            onInputChange={(_, newValue) => {
              setForm((p) => ({ ...p, giftName: newValue }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Gift Item Name"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              />
            )}
          />
        </Grid>
        <F label="Qty" name="giftQty" form={form} onChange={set} type="number" md={4} />

        {/* ── CHARGES ── */}
        <Grid item xs={12}>
          <Section icon={<ReceiptIcon fontSize="small" />} title="Charges" color="#4527a0" />
        </Grid>
        <F label="RTO (₹)"       name="rto"       form={form} onChange={set} type="number" startAdornment={rupee} md={6} />
        <F label="Insurance (₹)" name="insurance" form={form} onChange={set} type="number" startAdornment={rupee} md={6} />

        {/* ── PRICING ── */}
        <Grid item xs={12}>
          <Section icon={<CurrencyRupeeIcon fontSize="small" />} title="Pricing" color="#558b2f" />
        </Grid>
        <F label="Discount (₹)"        name="discount"       form={form} onChange={set} type="number" startAdornment={rupee} md={6} />
        <F label="Coupon Cashback (₹)" name="couponCashback" form={form} onChange={set} type="number" startAdornment={rupee} md={6} />
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{
            px: 2, py: "9px", borderRadius: 1.5, height: "40px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            bgcolor: netDisplay < 0 ? "#fff5f5" : "#f1f8e9",
            borderColor: netDisplay < 0 ? "#ef9a9a" : "#a5d6a7"
          }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Net Amount (ORP − Discount − Coupon)
            </Typography>
            <Typography variant="body2" fontWeight={700} color={netDisplay < 0 ? "error.main" : "#2e7d32"}>
              ₹ {netDisplay.toLocaleString("en-IN")}
            </Typography>
          </Paper>
        </Grid>

        {/* ── PAYMENT STATUS (DUE) ── */}
        <Grid item xs={12}>
          <Section icon={<PendingActionsIcon fontSize="small" />} title="Payment Status" color="#e65100" />
        </Grid>
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{
            px: 2, py: 1.5, borderRadius: 2,
            display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
            bgcolor: form.dueStatus === "DUE" ? "#fff8f5" : "#f1f8e9",
            borderColor: form.dueStatus === "DUE" ? "#ff7043" : "#66bb6a"
          }}>
            <Typography variant="body2" fontWeight={600} color="text.secondary">Status:</Typography>
            <ToggleButtonGroup
              value={form.dueStatus} exclusive onChange={handleDueStatus} size="small"
              sx={{ "& .MuiToggleButton-root": { px: 2.5, py: 0.5, fontWeight: 700, fontSize: "0.75rem", borderRadius: "6px !important", border: "1px solid", mx: 0.5 } }}
            >
              <ToggleButton value="PAID" sx={{ borderColor: "#66bb6a !important", "&.Mui-selected": { bgcolor: "#2e7d32 !important", color: "#fff" } }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 15, mr: 0.5 }} /> PAID
              </ToggleButton>
              <ToggleButton value="DUE" sx={{ borderColor: "#ff7043 !important", "&.Mui-selected": { bgcolor: "#e65100 !important", color: "#fff" } }}>
                <PendingActionsIcon sx={{ fontSize: 15, mr: 0.5 }} /> DUE
              </ToggleButton>
            </ToggleButtonGroup>
          </Paper>
        </Grid>

        <Collapse in={form.dueStatus === "DUE"} style={{ width: "100%" }}>
          <Grid container spacing={1.5} sx={{ pl: 1.5, pr: 0.75, mt: 0.5 }}>
            <F label="Total Due Amount *" name="dueTotalAmount" form={form} onChange={set}
              error={errors.dueTotalAmount} type="number" startAdornment={rupee} md={6}
              helperText="Amount customer still owes" />
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{
                px: 2, py: "9px", borderRadius: 1.5, height: "40px",
                display: "flex", alignItems: "center", justifyContent: "space-between",
                bgcolor: "#fff3e0", borderColor: "#ffb74d"
              }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Remaining at Sale Time
                </Typography>
                <Typography variant="body2" fontWeight={700} color="#e65100">
                  ₹ {dueRemaining !== null ? dueRemaining.toLocaleString("en-IN") : "—"}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Collapse>

        {/* existing due summary (read-only) */}
        {sale?.due && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "#f5f5f5" }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1}>
                CURRENT DUE SUMMARY (read-only)
              </Typography>
              {[
                ["Total",     `₹${(sale.due.totalAmount     || 0).toLocaleString("en-IN")}`],
                ["Paid",      `₹${(sale.due.paidAmount      || 0).toLocaleString("en-IN")}`],
                ["Remaining", `₹${(sale.due.remainingAmount || 0).toLocaleString("en-IN")}`],
                ["Status",    sale.due.status],
              ].map(([label, val]) => (
                <Box key={label} display="flex" justifyContent="space-between" py={0.3}
                  borderBottom="1px solid #ececec">
                  <Typography variant="body2" color="text.secondary">{label}</Typography>
                  <Typography variant="body2" fontWeight={700}
                    color={label === "Status" && val === "DUE" ? "error.main" : "inherit"}>
                    {val}
                  </Typography>
                </Box>
              ))}
            </Paper>
          </Grid>
        )}

        {/* ── SALESMAN ── */}
        <Grid item xs={12}>
          <Section icon={<PersonIcon fontSize="small" />} title="Salesman" color="#795548" />
        </Grid>
        <F label="Salesman Name"  name="salesmanName"      form={form} onChange={set} md={6} />
        <F label="Incentive (₹)" name="salesmanIncentive" form={form} onChange={set} type="number" startAdornment={rupee} md={6} />

        {/* ── REMARKS ── */}
        <Grid item xs={12}>
          <Section icon={<ReceiptIcon fontSize="small" />} title="Remarks" color="#546e7a" />
        </Grid>
        <F label="Remarks" name="remarks" form={form} onChange={set} xs={12} md={12} multiline rows={2} />

      </Grid>

      <Divider sx={{ my: 2 }} />

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleUpdate} disabled={loading}
          sx={{
            py: 1.2, px: 4, borderRadius: 2, fontWeight: 700,
            background: loading ? "#bdbdbd" : "linear-gradient(135deg, #1565c0, #42a5f5)",
            "&:hover": { background: loading ? "#bdbdbd" : "linear-gradient(135deg, #0d47a1, #1976d2)" }
          }}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {loading ? "Saving..." : "✅ Save Changes"}
        </Button>
      </Stack>
    </Box>
  );
};

export default UpdateSoldBikeForm;